import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { gaIdentiteit, lineQuantity, type CartItem } from "./types";
import { gekoppeldeArtikelen, metNieuwAantal, zonderRegelEnKoppeling } from "./cart-groups";
import { trackAddToCart } from "@/lib/track";

// v2: cart lines became a `kind`-discriminated union (glass | article). Old v1
// carts have no `kind` and are intentionally dropped (incompatible shape).
const STORAGE_KEY = "glastotaal.cart.v2";

// Distributive Omit so the discriminated union keeps its per-variant required fields.
export type NewCartItem = CartItem extends infer T ? (T extends CartItem ? Omit<T, "id"> : never) : never;

/** Laatst toegevoegde regel (voor de "toegevoegd"-melding). `n` verandert elke add. */
export interface LastAdded {
  title: string;
  image?: string;
  n: number;
}

interface CartApi {
  items: CartItem[];
  /** Total number of panes (sum of quantities). */
  count: number;
  /** `silent` onderdrukt de toevoeg-melding (bv. de configurator toont al de upsell-popup). */
  add: (item: NewCartItem, opts?: { silent?: boolean }) => void;
  /** Verwijdert de regel; bij een glasregel gaan de gekoppelde bij-artikelen mee. Retourneert wat
   *  er behalve de regel zelf is meegegaan, zodat de winkelwagen dat kan melden. */
  remove: (id: string) => CartItem[];
  setQuantity: (id: string, quantity: number) => void;
  clear: () => void;
  /** Laatst toegevoegde regel; door <CartToast> gebruikt voor de melding. */
  lastAdded: LastAdded | null;
}

const CartContext = createContext<CartApi | null>(null);

function load(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Defensive: keep only well-formed union lines.
    return parsed.filter((i): i is CartItem => !!i && (i.kind === "glass" || i.kind === "article" || i.kind === "benglas" || i.kind === "isolatieglas" || i.kind === "shower"));
  } catch {
    return [];
  }
}

const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

/** Twee losse artikelen zijn "dezelfde regel" als álle prijs-/config-velden gelijk zijn → dan
 *  voegen we ze samen (aantallen optellen) i.p.v. een dubbele regel. Glas/iso/etc. (maatwerk-
 *  configs) mergen we bewust nooit. */
function sameArticleLine(a: CartItem, b: NewCartItem): boolean {
  if (a.kind !== "article" || b.kind !== "article") return false;
  return (
    a.articleSlug === b.articleSlug &&
    a.lengthMm === b.lengthMm &&
    a.wMm === b.wMm &&
    a.hMm === b.hMm &&
    (a.note ?? "") === (b.note ?? "") &&
    !!a.montage === !!b.montage &&
    !!a.onRequest === !!b.onRequest &&
    JSON.stringify(a.surcharges ?? null) === JSON.stringify(b.surcharges ?? null) &&
    JSON.stringify(a.roosterConfig ?? null) === JSON.stringify(b.roosterConfig ?? null)
  );
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(load);
  const [lastAdded, setLastAdded] = useState<LastAdded | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // storage full / unavailable — keep the in-memory cart working.
    }
  }, [items]);

  // "Vaak samen besteld"-signaal: welke artikelen liggen samen in deze winkelwagen? Gedebounced,
  // zodat een reeks snelle toevoegingen één melding oplevert met de uiteindelijke samenstelling.
  //
  // Dynamische import: CartProvider omvat de hele app (nodig voor het winkelwagen-icoon op élke
  // pagina), dus een statische import hier trok de Functions-SDK in de kritieke eerste-lading-
  // bundel — voor een analytics-belletje dat pas 4s na een tweede artikel afgaat.
  useEffect(() => {
    const slugs = items.flatMap((i) => (i.kind === "article" && i.articleSlug ? [i.articleSlug] : []));
    if (new Set(slugs).size < 2) return;
    const timer = setTimeout(() => { void import("@/lib/cart-coadd").then((m) => m.logCartCoAdd(slugs)); }, 4000);
    return () => clearTimeout(timer);
  }, [items]);

  const add = useCallback((item: NewCartItem, opts?: { silent?: boolean }): void => {
    setItems((prev) => {
      // Identiek los artikel al in de wagen? → aantallen optellen i.p.v. dubbele regel.
      if (item.kind === "article") {
        const idx = prev.findIndex((i) => sameArticleLine(i, item));
        const ex = idx >= 0 ? prev[idx] : undefined;
        if (ex && ex.kind === "article") {
          const copy = prev.slice();
          copy[idx] = { ...ex, quantity: ex.quantity + item.quantity };
          return copy;
        }
      }
      return [...prev, { ...item, id: newId() } as CartItem];
    });
    if (!opts?.silent) {
      setLastAdded((p) => ({ title: item.display.title, image: item.display.imageUrl, n: (p?.n ?? 0) + 1 }));
    }
    // GA4 add_to_cart — item_id = artikel-slug (voor de relevantie-score); glas op maat op zijn
    // GLASSOORT (variant), zodat "bekeken" en "in winkelwagen" over hetzelfde gaan en je in de
    // rapporten per soort kunt kijken. Op `display.title` groeperen kan niet: die bevat de maten,
    // dus elke configuratie zou een eigen regel worden.
    const anyItem = item as { priceCents?: number; config?: { quantity?: number } };
    trackAddToCart({
      ...gaIdentiteit(item),
      price: typeof anyItem.priceCents === "number" ? anyItem.priceCents / 100 : undefined,
      quantity: anyItem.config?.quantity ?? 1,
    });
  }, []);

  /**
   * Een glasregel en de artikelen die de configurator eraan koppelde (rooster, beglazingsrubber,
   * scharnieren, knop) horen bij elkaar: het beslag is op die deur uitgezocht en heeft in z'n
   * eentje geen betekenis. Gooi je het glas weg, dan gaat de koppeling mee. Andersom niet — een
   * los scharnier weghalen mag zonder dat de deur verdwijnt.
   */
  const remove = useCallback((id: string): CartItem[] => {
    let mee: CartItem[] = [];
    setItems((prev) => {
      mee = gekoppeldeArtikelen(prev, id);
      return zonderRegelEnKoppeling(prev, id);
    });
    return mee;
  }, []);

  const setQuantity = useCallback((id: string, quantity: number): void => {
    setItems((prev) => metNieuwAantal(prev, id, quantity));
  }, []);

  /**
   * Winkelwagen legen.
   *
   * De vorige versie was `setItems([])`, en dat is elke keer een níeuwe lege array. React ziet dan
   * altijd een verandering, ook als de wagen al leeg was. Op de bedankpagina staat `clear()` in een
   * effect met `items` in de dependency-lijst; die bleef zichzelf zo eeuwig aanroepen — 113×
   * "Maximum update depth exceeded" en een pagina waar de knop "Verder winkelen" niet meer op
   * reageerde (gemeld 26-09-2026). Dezelfde array teruggeven laat React de update overslaan.
   */
  const clear = useCallback((): void => setItems((huidig) => (huidig.length === 0 ? huidig : [])), []);

  const value = useMemo<CartApi>(
    () => ({ items, count: items.reduce((n, i) => n + lineQuantity(i), 0), add, remove, setQuantity, clear, lastAdded }),
    [items, add, remove, setQuantity, clear, lastAdded],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartApi {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
